import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, Clock, ArrowRight, Search, ChevronLeft, ChevronRight, 
  Eye, Heart, MessageCircle, Filter, X, TrendingUp, Bookmark, BookmarkCheck 
} from "lucide-react";
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from "react-icons/fa";

const Blog = () => {
  // --- STATE MANAGEMENT ---
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("latest");
  const [bookmarkedPosts, setBookmarkedPosts] = useState([]);
  const postsPerPage = 6;

  // --- FETCH DATA FROM API ---
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
       
        const apiUrl = process.env.REACT_APP_API_URL || 'https://crm.cashpro.co.in'; 
        // Vite users: const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';

        const response = await fetch(`${apiUrl}/api/blogs`);
        const result = await response.json();

        if (result.success) {
          
          const formattedData = result.data.map((post) => ({
            id: post.id,
            title: post.title,
            excerpt: post.excerpt || "Read more about this topic inside...",
            image: post.thumbnail_url || "https://placehold.co/600x400/22c55e/white?text=CashPro+Blog",
            author: post.author_id === 'admin' ? 'CashPro Admin' : 'Financial Expert',
            date: new Date(post.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }),
            readTime: "5 min read", // Dynamic word count logic aage add kar sakte hain
            category: post.category_name,
            path: `/blog/${post.id}`,
            tags: ["Finance", "CashPro"], // Default tags
            // Dummy stats jab tak backend me inka database column na ban jaye
            views: Math.floor(Math.random() * 500) + 100,
            likes: Math.floor(Math.random() * 50) + 10,
            comments: Math.floor(Math.random() * 10) + 1
          }));
          
          setBlogPosts(formattedData);
        } else {
          setError(result.message);
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError("Failed to connect to the server. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // --- LOGIC & FILTERING ---
  const getCategoryCount = (category) => {
    if (category === "All") return blogPosts.length;
    return blogPosts.filter(post => post.category === category).length;
  };

  const categories = ["All", ...new Set(blogPosts.map(post => post.category))];

  const toggleBookmark = (postId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (bookmarkedPosts.includes(postId)) {
      setBookmarkedPosts(bookmarkedPosts.filter(id => id !== postId));
    } else {
      setBookmarkedPosts([...bookmarkedPosts, postId]);
    }
  };

  let filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (sortBy === "latest") {
    filteredPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
  } else if (sortBy === "popular") {
    filteredPosts.sort((a, b) => b.views - a.views);
  } else if (sortBy === "mostLiked") {
    filteredPosts.sort((a, b) => b.likes - a.likes);
  }

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const popularPosts = [...blogPosts].sort((a, b) => b.views - a.views).slice(0, 4);

  // --- LOADING & ERROR UI ---
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 text-red-500 font-semibold">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-green-700 via-green-600 to-emerald-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Financial Insights Blog</h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto mb-8">
            Expert advice, tips, and guides to help you make smart financial decisions
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-12 pr-12 py-3 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 shadow-lg"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500">
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                    <Filter size={16} /> Filters
                  </button>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                    <option value="mostLiked">Most Liked</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">{filteredPosts.length} articles found</div>
              </div>
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => (
                      <button key={category} onClick={() => { setSelectedCategory(category); setCurrentPage(1); }} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === category ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                        {category} ({getCategoryCount(category)})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Blog Cards Grid */}
            {currentPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-5">
                {currentPosts.map((post) => (
                  <Link key={post.id} to={post.path} className="block bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition group">
                    <div className="relative h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                      <div className="absolute top-2 right-2">
                        <button onClick={(e) => toggleBookmark(post.id, e)} className="bg-white/90 p-1.5 rounded-full hover:bg-yellow-400 transition z-10">
                          {bookmarkedPosts.includes(post.id) ? <BookmarkCheck size={14} className="text-yellow-500" /> : <Bookmark size={14} />}
                        </button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-green-600 text-white px-2 py-0.5 rounded text-xs">{post.category}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <Calendar size={11} /> {post.date}
                        <span className="mx-1">•</span>
                        <Clock size={11} /> {post.readTime}
                      </div>
                      <h3 className="font-bold text-gray-800 mb-1 group-hover:text-green-600 line-clamp-2 text-base">{post.title}</h3>
                      <p className="text-gray-600 text-xs mb-3 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Eye size={11} /> {post.views}</span>
                          <span className="flex items-center gap-1"><Heart size={11} /> {post.likes}</span>
                          <span className="flex items-center gap-1"><MessageCircle size={11} /> {post.comments}</span>
                        </div>
                        <span className="text-green-600 text-xs font-semibold flex items-center gap-1">Read <ArrowRight size={11} /></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Search size={40} className="text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No articles found.</p>
                <button onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }} className="mt-3 text-green-600 text-sm">Clear filters</button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">
                  <ChevronLeft size={18} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button key={i} onClick={() => setCurrentPage(i + 1)} className={`w-8 h-8 rounded-lg transition ${currentPage === i + 1 ? "bg-green-600 text-white" : "border hover:bg-gray-100"}`}>
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-5">
            {/* Most Popular */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                <TrendingUp size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800">Most Popular</h3>
              </div>
              <div className="space-y-3">
                {popularPosts.length > 0 ? popularPosts.map((post, index) => (
                  <Link key={post.id} to={post.path} className="flex gap-3 group">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1">
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-bold">#{index + 1}</span>
                        <span className="text-xs text-gray-400">{post.views} views</span>
                      </div>
                      <h4 className="font-semibold text-gray-800 group-hover:text-green-600 text-sm line-clamp-2">{post.title}</h4>
                    </div>
                  </Link>
                )) : <p className="text-sm text-gray-500">No popular posts yet.</p>}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">Categories</h3>
              <div className="space-y-1">
                {categories.map(category => (
                  <button key={category} onClick={() => { setSelectedCategory(category); setCurrentPage(1); }} className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition flex justify-between items-center ${selectedCategory === category ? "bg-green-50 text-green-600 font-medium" : "text-gray-600 hover:bg-gray-50"}`}>
                    <span>{category}</span>
                    <span className="text-xs text-gray-400">{getCategoryCount(category)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Subscribe */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-5 text-white">
              <h3 className="font-bold text-lg mb-1">Subscribe Now</h3>
              <p className="text-green-100 text-sm mb-3">Get latest financial tips</p>
              <input type="email" placeholder="Your email address" className="w-full px-3 py-2 rounded-lg text-gray-800 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              <button className="w-full bg-yellow-400 text-gray-800 py-2 rounded-lg font-semibold text-sm hover:bg-yellow-300 transition">Subscribe</button>
            </div>

            {/* Social Follow */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">Follow Us</h3>
              <div className="flex justify-around">
                <a href="#" className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:scale-110 transition">
                  <FaFacebook size={18} />
                </a>
                <a href="#" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:scale-110 transition">
                  <FaTwitter size={18} />
                </a>
                <a href="#" className="w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center hover:scale-110 transition">
                  <FaInstagram size={18} />
                </a>
                <a href="#" className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:scale-110 transition">
                  <FaLinkedin size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Blog;